import { Box, DialogTitle, Divider, IconButton, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ArrowLeftOMIcon, CloseOMIcon } from '@omnea-app/icons'
import type { ReactNode } from 'react'

type Props = {
  title: string
  subtitle?: string
  onClose?: () => void
  /** Set when the dialog navigates within itself, so the title can be stepped back out of. */
  onBack?: () => void
  /** Use while a save is in flight, so the dialog cannot be dismissed mid-mutation. */
  disabled?: boolean
  /** Sits right after the title, such as a control that brings back a hidden panel. */
  titleAction?: ReactNode
  /** Pushed to the end, before the close button, such as the dialog's save. */
  actions?: ReactNode
  /** Rules the header off from the content below, for a full-screen dialog whose content has no edge of its own. */
  divider?: boolean
}

/** Dialog counterpart of DrawerHeader: the themed DialogTitle with a close affordance pushed to the end. */
export const DialogHeader = ({ title, subtitle, onClose, onBack, disabled, titleAction, actions, divider }: Props) => {
  const { t } = useTranslation('common')

  // Shrinkable, so an arbitrarily long title ellipsizes rather than squeezing the close button
  const titleNode = (
    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {title}
    </Box>
  )

  const header = (
    <DialogTitle sx={{ alignItems: 'center', gap: 2 }}>
      {onBack && (
        <IconButton onClick={onBack} size="medium" aria-label={t('dialog.back')} disabled={disabled}>
          <ArrowLeftOMIcon />
        </IconButton>
      )}
      {subtitle ? (
        <Stack component="span" minWidth={0}>
          {titleNode}
          <Typography component="span" variant="body-regular" color="text.muted">
            {subtitle}
          </Typography>
        </Stack>
      ) : (
        titleNode
      )}
      {titleAction}
      {(actions || onClose) && (
        <Stack direction="row" alignItems="center" gap={2} ml="auto">
          {actions}
          {onClose && (
            <IconButton onClick={onClose} size="medium" aria-label={t('dialog.close')} disabled={disabled}>
              <CloseOMIcon />
            </IconButton>
          )}
        </Stack>
      )}
    </DialogTitle>
  )

  return divider ? (
    <>
      {header}
      <Divider />
    </>
  ) : (
    header
  )
}
